import { getUserConfiguration } from 'owa-session-store';
import { lazySendAutomatedMessage, MessageDetails } from 'owa-automated-message';
import { searchDiagnosticsStore } from 'owa-search-diagnostics';
import { SearchRequestInstrumentation } from 'owa-search-service';
import { userVoiceSuggestionStore } from 'owa-uservoice';
import { UserVoiceSearchTeam } from '../components/SearchInlineFeedback';
import { featureFlags } from 'owa-feature-flags';

export default async (
    userVoiceFeedbackTeam: UserVoiceSearchTeam,
    inlineFeedbackResponse?: string,
    userVoiceInlineFeedbackTags?: string[]
) => {
    const sendAutomatedMessage = await lazySendAutomatedMessage.import();
    const messageDetails: MessageDetails = {
        subject: 'OWA 3S Query Feedback',
        message: buildMessage(
            inlineFeedbackResponse,
            userVoiceInlineFeedbackTags,
            userVoiceFeedbackTeam
        ),
        to:
            userVoiceFeedbackTeam == UserVoiceSearchTeam.CalendarSearch
                ? ['owasearchdog@microsoft.com']
                : ['owa3squeryfeedback@service.microsoft.com'],
        attachments: null,
    };

    const timeFormat = getUserConfiguration().UserOptions.TimeFormat;

    sendAutomatedMessage(messageDetails, timeFormat);
};

const buildMessage = (
    inlineFeedbackResponse: string,
    userVoiceInlineFeedbackTags: string[],
    userVoiceFeedbackTeam: UserVoiceSearchTeam
) => {
    const feedbackCategories = userVoiceInlineFeedbackTags
        ? userVoiceInlineFeedbackTags
              .filter(c => c != '')
              .map(convertUserVoiceInlineFeedbackTagsToCheckListItem)
              .join('\n')
        : userVoiceSuggestionStore.customFields
              .map(convertFeedbackCategoryToChecklistItem)
              .join('\n');

    const searchInstrumentationEntries = searchDiagnosticsStore.searchInstrumentationList.slice();

    searchInstrumentationEntries.forEach(instrumentation => {
        // Strip out PII data
        delete instrumentation.PIIData;

        // Add resource timing entries to top level of the object
        if (instrumentation.ResourceTimingEntry) {
            Object.keys(instrumentation.ResourceTimingEntry).forEach(key => {
                // Skip the rollup resource timing data
                if (key == 'cpv') {
                    return;
                }

                instrumentation[key] = instrumentation.ResourceTimingEntry[key];
            });
        }
        delete instrumentation.ResourceTimingEntry;
    });

    const searchInstrumentation = convertAllInstrumentationToTable(searchInstrumentationEntries);

    const errorInformation = buildErrorInformation(searchInstrumentationEntries);
    const errorText = errorInformation ? `<h2>Error Information</h2>${errorInformation}\n\n\n` : '';

    const verbatimeMessage = inlineFeedbackResponse
        ? inlineFeedbackResponse
        : userVoiceSuggestionStore.message;

    // Get enabled search feature flags to help debug.
    const searchFlagsEnabled: string[] = getFeatureFlagsByPrefix('sea');
    if (userVoiceFeedbackTeam == UserVoiceSearchTeam.CalendarSearch) {
        const calendarSearchFlags = getFeatureFlagsByPrefix('cal-sea');
        searchFlagsEnabled.push(...calendarSearchFlags);
    }

    const message = `<h2>Categories</h2>${feedbackCategories} \n\n\n
        <h2>Verbatim</h2>${verbatimeMessage}\n\n\n
        ${errorText}
        <h2>Search History</h2>${searchInstrumentation.replace(/\n/g, '')}\n\n\n
        <h2>Client Search Flights Enabled</h2>${searchFlagsEnabled.join(', ')}`;
    return message.replace(/\n/g, '<br />');
};

const buildErrorInformation = (instrumentation: SearchRequestInstrumentation[]): string => {
    if (!instrumentation || instrumentation.length === 0) {
        return null;
    }

    const lastRequest = instrumentation[0];
    const lastErrorContent = lastRequest.SearchErrorContent;

    if (!lastErrorContent) {
        return null;
    }

    try {
        const error = JSON.parse(JSON.parse(lastErrorContent).MessageText);
        return `
            <strong>HTTP code:</strong> ${error.httpCode}\n
            <strong>Code:</strong> ${error.code}\n
            <strong>Description:</strong> ${error.description}\n
        `;
    } catch (e) {
        return null;
    }
};

const convertUserVoiceInlineFeedbackTagsToCheckListItem = c =>
    `${String.fromCharCode(9745)}&nbsp;${c}`;

const convertFeedbackCategoryToChecklistItem = c =>
    `${c.Value ? String.fromCharCode(9745) : String.fromCharCode(9744)}&nbsp;${c.Name}`;

const convertAllInstrumentationToTable = (
    instrumentation: SearchRequestInstrumentation[]
): string =>
    instrumentation == null || instrumentation.length == 0
        ? ''
        : `<table border="1">
            ${extractInstrumentationTableHeaders(instrumentation)}
            ${convertInstrumentationToTableRows(instrumentation)}
        </table>`;

const getDisplayFieldForKey = (key: string): string => {
    if (key == 'SearchQueryId') {
        return 'SearchConversationId';
    }

    return key;
};

const extractInstrumentationTableHeaders = (
    instrumentation: SearchRequestInstrumentation[]
): string =>
    `<thead><tr>
        ${Object.keys(instrumentation[0])
            .map(key => `<th>${getDisplayFieldForKey(key)}</th>`)
            .join('')}
    </tr></thead>`;

const convertInstrumentationToTableRows = (
    instrumentation: SearchRequestInstrumentation[]
): string =>
    instrumentation
        .map(
            e =>
                `<tr>
                    ${Object.keys(e)
                        .map(key => convertInstrumentationFieldToTableCell(e[key]))
                        .join('')}
                </tr>`
        )
        .join('');

const convertInstrumentationFieldToTableCell = (field: any): string => {
    if (field === undefined || field === null) {
        return '<td></td>';
    } else if (field instanceof Array) {
        return `<td>${field.join(',')}</td>`;
    } else if (field instanceof Date) {
        return `<td>${field.toISOString()}</td>`;
    } else {
        return `<td>${field.toString()}</td>`;
    }
};

const getFeatureFlagsByPrefix = (prefix: string): string[] => {
    const enabledFeatureFlags = [];

    featureFlags.forEach((isEnabled: boolean, featureName: string) => {
        if (featureName.indexOf(prefix) === 0 && isEnabled) {
            enabledFeatureFlags.push(featureName);
        }
    });

    return enabledFeatureFlags.sort();
};
